import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Building2, User, LogOut, LayoutDashboard, Users, Settings, ChevronDown } from 'lucide-react';
import { Toaster } from './ui/sonner';

const Layout = ({ children }) => {
  const { user, logout, isAdmin, isSuperAdmin, isManager } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { 
      label: 'My Review', 
      path: '/employee', 
      icon: LayoutDashboard,
      show: true 
    },
    { 
      label: 'Team Reviews', 
      path: '/manager', 
      icon: Users,
      show: isManager() 
    },
    { 
      label: 'Admin', 
      path: '/admin', 
      icon: Settings,
      show: isAdmin() 
    },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#007AFF] flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div className="hidden sm:block">
                <span className="font-semibold text-lg">HR Performance</span>
                {user?.company && (
                  <span className="ml-2 text-xs text-gray-400 font-normal">
                    {isSuperAdmin() ? `[${user.company.name}]` : `· ${user.company.name}`}
                  </span>
                )}
              </div>
            </Link>

            {/* Nav Links */}
            <div className="hidden md:flex items-center gap-1">
              {navItems.filter(item => item.show).map((item) => {
                const isActive = location.pathname.startsWith(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive 
                        ? 'bg-white/10 text-white' 
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-4 h-4 inline mr-2" />
                    {item.label}
                  </Link>
                );
              })}
            </div>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 hover:bg-white/5" data-testid="user-menu-btn">
                  <div className="w-8 h-8 rounded-full bg-[#007AFF]/20 flex items-center justify-center">
                    <User className="w-4 h-4 text-[#007AFF]" />
                  </div>
                  <span className="hidden sm:block text-sm">{user?.name || user?.email?.split('@')[0]}</span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[#1E1E1E] border-white/10">
                <DropdownMenuLabel className="text-gray-400">
                  <div className="font-normal text-xs">Signed in as</div>
                  <div className="font-medium text-white truncate">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                
                {/* Mobile nav items */}
                <div className="md:hidden">
                  {navItems.filter(item => item.show).map((item) => (
                    <DropdownMenuItem 
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className="cursor-pointer hover:bg-white/5"
                    >
                      <item.icon className="w-4 h-4 mr-2" />
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator className="bg-white/10" />
                </div>
                
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="cursor-pointer text-red-400 hover:bg-red-500/10 hover:text-red-400"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Toast notifications */}
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#1E1E1E',
            border: '1px solid rgba(255,255,255,0.1)',
            color: 'white',
          },
        }}
      />
    </div>
  );
};

export default Layout;
